import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import type { ComplianceActor, ComplianceFileStatus } from '@/shared/hooks/api/lvdComplianceFiles'
import type {
  ConfirmedHarmonisedStandard,
  HarmonisedStandardRecommendation,
} from '@/v2/lib/confirmedHarmonisedStandards'

export const EMC_PRODUCT_COMPLIANCE_FILES_QUERY_KEY =
  'emcProductComplianceFiles' as const

export type EmcProductComplianceFileView = {
  id: string
  status: ComplianceFileStatus
  completedAt: string | null
  completedByActor: ComplianceActor | null
  confirmedStandardsJson: ConfirmedHarmonisedStandard[] | null
  latestOutputDocumentId: string | null
  latestDeclarationNumber: string | null
  createdAt: string
  updatedAt: string
}

export type EmcProductComplianceFileOutputDocumentStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'

export type EmcProductComplianceFileOutputDocument = {
  id: string
  emcProductComplianceFileId: string
  documentType: string
  declarationNumber: string | null
  fileName: string
  fileSizeKb: number | null
  contentType: string | null
  createdByActor: ComplianceActor | null
  createdByUserId: string | null
  status: EmcProductComplianceFileOutputDocumentStatus
  errorMessage: string | null
  statusUpdatedAt: string | null
  workflowRunId: string | null
  createdAt: string
  updatedAt: string
}

export type EmcProductComplianceFilesState = {
  product: EmcProductComplianceFileView | null
  outputDocuments: EmcProductComplianceFileOutputDocument[]
}

export function useGetEmcProductComplianceFiles(
  productId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<EmcProductComplianceFilesState, Error>({
    queryKey: [EMC_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
    queryFn: async () => {
      const response = await apiClient.get<EmcProductComplianceFilesState>(
        `/v2/products/${productId}/emc/compliance-file`
      )
      return response.data
    },
    enabled:
      (options?.enabled ?? true) && productId != null && productId.length > 0,
  })
}

export type CompleteEmcProductComplianceFileResult = {
  product: EmcProductComplianceFileView
  warnings: string[]
}

export type UncompleteEmcProductComplianceFileResult = {
  product: EmcProductComplianceFileView
}

export function useCompleteEmcProductComplianceFile(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<CompleteEmcProductComplianceFileResult, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post<CompleteEmcProductComplianceFileResult>(
        `/v2/products/${productId}/emc/compliance-file/complete`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EMC_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export function useUncompleteEmcProductComplianceFile(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<UncompleteEmcProductComplianceFileResult, Error, void>({
    mutationFn: async () => {
      const response =
        await apiClient.post<UncompleteEmcProductComplianceFileResult>(
          `/v2/products/${productId}/emc/compliance-file/uncomplete`
        )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EMC_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export type ReplaceEmcConfirmedStandardsResult = {
  product: EmcProductComplianceFileView
  standards: ConfirmedHarmonisedStandard[]
}

export type EmcStandardsRecommendationsView = {
  standards: HarmonisedStandardRecommendation[]
}

export function useReplaceEmcConfirmedStandards(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<
    ReplaceEmcConfirmedStandardsResult,
    Error,
    { standards: ConfirmedHarmonisedStandard[] }
  >({
    mutationFn: async (payload) => {
      const response = await apiClient.put<ReplaceEmcConfirmedStandardsResult>(
        `/v2/products/${productId}/emc/compliance-file/confirmed-standards`,
        payload
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData<EmcProductComplianceFilesState>(
        [EMC_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
        (current) => ({
          product: data.product,
          outputDocuments: current?.outputDocuments ?? [],
        })
      )
      void queryClient.invalidateQueries({
        queryKey: [EMC_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export function useGetEmcStandardsRecommendations(productId: string) {
  return useMutation<EmcStandardsRecommendationsView, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post<EmcStandardsRecommendationsView>(
        `/v2/products/${productId}/emc/compliance-file/standards-recommendations`
      )
      return response.data
    },
  })
}

const EMC_PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_GENERATE_TIMEOUT_MS =
  180_000

type EmcProductComplianceFileDeclarationOfConformityStreamEvent =
  | { type: 'workflow.started' }
  | { type: 'step.started'; step: string }
  | { type: 'step.completed'; step: string }
  | { type: 'progress'; message: string; temporary?: boolean }
  | {
      type: 'workflow.completed'
      result?: {
        outputDocumentId: string
        declarationNumber: string
        fileName: string
      }
    }
  | { type: 'error'; error: string }
  | { type: 'workflow.failed'; error?: string }

function getApiStreamUrl(path: string): string {
  const baseURL = apiClient.defaults.baseURL ?? ''
  return `${baseURL}${path}`
}

function getApiStreamHeaders(): Record<string, string> {
  const common = (apiClient.defaults.headers.common ?? {}) as Record<
    string,
    string
  >
  const { 'Content-Type': _contentType, ...rest } = common
  return rest
}

async function downloadEmcProductComplianceFileOutputDocumentFromUrl(
  downloadUrl: string,
  fileName: string
) {
  const response = await fetch(downloadUrl)
  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`)
  }
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  link.click()
  URL.revokeObjectURL(objectUrl)
}

export function useDownloadEmcProductComplianceFileOutputDocument(
  productId: string
) {
  return useMutation<
    void,
    Error,
    { outputDocumentId: string; fileName?: string }
  >({
    mutationFn: async ({ outputDocumentId, fileName }) => {
      const response = await apiClient.get<{
        downloadUrl: string
        fileName: string
        declarationNumber: string | null
      }>(
        `/v2/products/${productId}/emc/compliance-file/output-documents/${outputDocumentId}/download-url`
      )
      await downloadEmcProductComplianceFileOutputDocumentFromUrl(
        response.data.downloadUrl,
        fileName ?? response.data.fileName
      )
    },
  })
}

export function useStartEmcProductComplianceFileDeclarationOfConformityGeneration(
  productId: string
) {
  const queryClient = useQueryClient()

  const invalidateFiles = () => {
    void queryClient.invalidateQueries({
      queryKey: [EMC_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
    })
  }

  return useMutation<
    void,
    Error,
    {
      manufacturerName: string
      manufacturerAddress: string
      onEvent?: (
        event: EmcProductComplianceFileDeclarationOfConformityStreamEvent
      ) => void
      onComplete?: (result: {
        outputDocumentId: string
        declarationNumber: string
        fileName: string
      }) => void
    }
  >({
    mutationFn: async ({
      manufacturerName,
      manufacturerAddress,
      onEvent,
      onComplete,
    }) => {
      const url = getApiStreamUrl(
        `/v2/products/${productId}/emc/compliance-file/declaration-of-conformity/generate`
      )
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getApiStreamHeaders(),
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          manufacturerName,
          manufacturerAddress,
        }),
        signal: AbortSignal.timeout(
          EMC_PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_GENERATE_TIMEOUT_MS
        ),
      })
      if (!response.ok) {
        throw new Error(
          `Failed to start Declaration of Conformity generation: ${response.statusText}`
        )
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }

      invalidateFiles()

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let hasInvalidatedOnProgress = false

      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            const event = JSON.parse(
              data
            ) as EmcProductComplianceFileDeclarationOfConformityStreamEvent & {
              type: string
            }
            if (
              !hasInvalidatedOnProgress &&
              (event.type === 'workflow.started' ||
                event.type === 'step.started' ||
                event.type === 'progress')
            ) {
              hasInvalidatedOnProgress = true
              invalidateFiles()
            }
            if (
              event.type === 'progress' ||
              event.type === 'step.started' ||
              event.type === 'step.completed' ||
              event.type === 'error'
            ) {
              onEvent?.(event)
            }
            if (event.type === 'workflow.completed') {
              const completedEvent = event as Extract<
                EmcProductComplianceFileDeclarationOfConformityStreamEvent,
                { type: 'workflow.completed' }
              >
              if (completedEvent.result) {
                await onComplete?.(completedEvent.result)
              }
              return
            }
            if (event.type === 'error') {
              throw new Error(event.error ?? 'Generation failed')
            }
            if (event.type === 'workflow.failed') {
              throw new Error(
                (event as { error?: string }).error ?? 'Workflow failed'
              )
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    },
    onSuccess: () => {
      invalidateFiles()
    },
    onError: () => {
      invalidateFiles()
    },
  })
}
